// External imports
import React, { Fragment, useEffect, useRef } from 'react';
import {
  Typography,
  Button,
  withStyles,
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableCell,
  Menu,
  MenuItem,
  Grid,
} from '@material-ui/core';
import ReactPaginate from 'react-paginate';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningAmberIcon from '@material-ui/icons/ReportProblemTwoTone';
import CheckIcon from '@material-ui/icons/Check';
// Internal imports
import { statusOK } from '~/app/webHelpers.js';
import ClickableTypography from '#/Common/ClickableTypography.jsx';
import AddDocumentModal from './Modals/AddDocumentModal.jsx';
import PreviewDocumentModal from './Modals/PreviewDocumentModal.jsx';
import RequestPDFModal from './Modals/RequestPDFModal.jsx';
import XlinkAPI from '~/app/api/xlinkAPI';
import InvoiceAPI from '~/app/api/remoteInvoiceAPI.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import { useSetState } from '~/app/Utility/customHooks';
import SimpleDialog from '#/Common/SimpleDialog.jsx';
import {
  COLUMN_HEADERS,
  REMOTE_COLUMN_HEADERS,
  REQUESTEE_TYPE,
  REMOTE_DOC_STATUS_LABELS,
  UNDELETABLE_DOC_TYPES,
  ARCHIVE_EMAIL_BODY,
} from '~/app/Pages/Returns/components/Document/ArchivedDocuments/archivedDocumentsConstants.js';
import {
  getRemoteDocumentList,
  buildPreviewString,
  convertTimestampToLocal,
} from '~/app/Pages/Returns/components/Document/ArchivedDocuments/archivedDocumentsHelpers';
import { formatMaskSSNEIN } from '~/app/Utility/general.js';
import { LOAD_STATUS } from '~/app/constants.js';
import {
  printList,
  // Aliased: `maskSSNEIN` is already taken here by the office's on-screen mask preference.
  maskSSNEIN as maskSSNEINForPrint,
} from '~/app/Pages/Returns/components/Print/printListHelpers.js';
import PrintListButton from '~/app/Pages/Returns/components/Print/PrintListButton.jsx';
import AccessControl from '~/app/Components/Auth/AccessControl.jsx';
// Redux imports
import { useSelector, useDispatch } from 'react-redux';
import { actions as appActions } from '~/app/redux/modules/app.js';
import { store } from '~/app/redux/index';
import { actions as remoteDocActions } from '~/app/redux/modules/remoteDocRequests.js';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer';
// Styling imports
import { styles } from '~/app/Pages/Returns/components/Document/ArchivedDocuments/css/archivedDocuments.js';
import './css/archivedDocuments.css';
import RequestDocumentsModal from './Modals/RequestDocumentsModal.jsx';
// Image imports
import menuVertical from '~/images/icons/menu_vertical.png';

/**
 * Normalizes a list response body. These endpoints report "no rows" as a null body
 * (Go marshals a nil slice to null), which is why the fetches below check the status
 * only - but that also lets a malformed body through, and calling it an empty list
 * would print "none" as fact. Null becomes an empty list; anything that is not an
 * array is rejected.
 *
 * @param {*} data response body
 * @returns {Array|null} the rows, or null when the body is not a list
 */
const asRowArray = data => {
  if (data === null || data === undefined) return [];
  return Array.isArray(data) ? data : null;
};

const ACTIVE_TAB = {
  ARCHIVE: 'archive',
  REQUESTED: 'requested',
  INVOICES: 'invoices',
};

/**
 * component allows users to "Add New Documents" to be displayed in a table.
 * The table displays rows of documents and their information that the user added.
 * They then can be "Previewed" as a PDF then downloaded
 *
 * @component ArchivedDocuments
 * @category ArchivedDocuments
 **/
const ArchivedDocuments = props => {
  const { classes } = props;
  const dispatch = useDispatch();
  const {
    returnID,
    firmName,
    taxpayerEmailForPdf,
    spouseEmailForPdf,
    taxpayerInformation,
    clientFilingStatus,
    remoteDocRequestsList,
    isEditingRMD,
    currentRequestStep,
    currentPage,
    totalRequestsCount,
    rmdFormFields,
    documentTypes,
    maskSSNEIN,
  } = useSelector(state => ({
    returnID: state.returnProfile.returnID,
    firmName: state.officeProfile.firm_name,
    taxpayerEmailForPdf: state.formViewer.taxpayerEmailForPdf,
    spouseEmailForPdf: state.formViewer.spouseEmailForPdf,
    // Re-created (new object reference) on every return_save, so it serves as a
    // reliable "save just completed" signal even when the email comes back empty.
    taxpayerInformation: state.formViewer.taxpayerInformation,
    clientFilingStatus: state.returnProfile.clientFilingStatus,
    remoteDocRequestsList: state.remoteDocRequests.remoteDocRequestsList,
    isEditingRMD: state.remoteDocRequests.isEditingRMD,
    currentRequestStep: state.remoteDocRequests.currentRequestStep,
    currentPage: state.remoteDocRequests.currentPage,
    totalRequestsCount: state.remoteDocRequests.totalRequestsCount,
    rmdFormFields: state.remoteDocRequests.rmdFormFields,
    documentTypes: state.formViewer.documentTypes,
    maskSSNEIN: state.defaultsPageState.maskSSNEIN,
  }));
  // Holds the archive row we intend to email while waiting for return_save to populate the email addresses
  const pendingEmailRowRef = useRef(null);

  const [state, setState] = useSetState({
    currentOpenAttachment: null,
    currentOpenAttachmentDesc: null,
    currentOpenAttachmentType: null,
    currentOpenAttachmentDateUp: null,
    contextMenuAnchor: null,
    contextMenuSelected: null,
    docID: null,
    isAddDocumentModalOpen: false,
    isRequestPDFModalOpen: false,
    isPreviewModalOpen: false,
    isConfirmDeleteModalOpen: false,
    isDocArchiveConfirmDeleteModalOpen: false,
    isNoEmailModalOpen: false,
    selectedHeader: 0,
    getReturnGUID: '',
    walletToken: null,
    secToken: null,
    remoteInvoices: [],
    remoteDocsStatus: LOAD_STATUS.PENDING,
    remoteInvoicesStatus: LOAD_STATUS.PENDING,
    documentTypesStatus: LOAD_STATUS.PENDING,
  });

  useEffect(() => {
    props.includeCompletedRS();
    fetchRemoteRequestsCount(returnID);
    fetchRemoteDocuments(returnID, currentPage);
    getWalletToken();

    // populate the selectable document types once.
    if (documentTypes?.length) {
      // Already populated by an earlier return - nothing to wait for.
      setState({ documentTypesStatus: LOAD_STATUS.LOADED });
    } else {
      // Covers both the empty lookup and a null/undefined one: falling through without
      // fetching would leave the status pending and Print disabled for good.
      fetchDocumentTypeList();
    }
  }, []);

  useEffect(() => {
    if (state.walletToken) {
      fetchRemoteInvoices();
    }
  }, [state.walletToken]);

  /** fetches the document types from the document type lookup table */
  // TODO: Update Add Documents modal to use the updated documentType logic instead of hardcoding the list
  const fetchDocumentTypeList = async () => {
    setState({ documentTypesStatus: LOAD_STATUS.PENDING });
    try {
      const res = await XlinkAPI.getDocumentTypeList();

      if (statusOK(res) && Array.isArray(res.data) && res.data.length) {
        dispatch(formViewerActions.setDocumentTypes(res.data));
        setState({ documentTypesStatus: LOAD_STATUS.LOADED });
      } else {
        setState({ documentTypesStatus: LOAD_STATUS.FAILED });
        // Raise a dialog only for a genuinely failed request. An empty lookup is not
        // expected either, but it is not worth a blocking dialog on every mount.
        if (!statusOK(res)) {
          ErrorHelpers.handleError(
            'Error fetching document types',
            ErrorHelpers.createSimpleError('Unable to load document types. Please try again.'),
          );
        }
      }
    } catch (error) {
      dispatch(
        appActions.showSnackbarMessage(error, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Error opening attachment', error);
      setState({ documentTypesStatus: LOAD_STATUS.FAILED });
    }
  };

  const getWalletToken = async () => {
    try {
      const walletData = await props.getWalletToken(true);

      if (walletData?.walletToken) {
        setState({
          walletToken: walletData.walletToken,
          secToken: walletData.secToken,
        });
      } else {
        setState({ remoteInvoicesStatus: LOAD_STATUS.FAILED });
      }
    } catch (error) {
      dispatch(
        appActions.showSnackbarMessage(error, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Error fetching Wallet Token', error);
      setState({ remoteInvoicesStatus: LOAD_STATUS.FAILED });
    }
  };

  const fetchRemoteInvoices = async () => {
    try {
      const res = await XlinkAPI.getInvoicesByReturnID(returnID);

      const remoteInvoices = asRowArray(res.data); // array of invoice objects
      if (!statusOK(res, false) || !remoteInvoices) {
        setState({ remoteInvoicesStatus: LOAD_STATUS.FAILED });
      } else if (remoteInvoices.length > 0) {
        try {
          const fetchedDataList = await Promise.all(
            remoteInvoices.map(invoice => getRemoteInvoiceByInvoiceGuid(invoice?.invoiceGuid)),
          );

          const hasEveryInvoice = fetchedDataList.every(invoiceData => invoiceData !== undefined);

          remoteInvoices.forEach((_, index) => {
            remoteInvoices[index] = {
              ...remoteInvoices[index],
              invoiceData: fetchedDataList[index],
            };
          });

          setState({
            remoteInvoices: remoteInvoices?.reverse() || [],
            remoteInvoicesStatus: hasEveryInvoice ? LOAD_STATUS.LOADED : LOAD_STATUS.FAILED,
          });
        } catch (err) {
          setState({ remoteInvoicesStatus: LOAD_STATUS.FAILED });
          ErrorHelpers.handleError('Failed to Retrieve Remote Invoice Data: ', err?.message);
        }
      } else {
        // Successfully established that this return has no remote invoices.
        setState({ remoteInvoicesStatus: LOAD_STATUS.LOADED });
      }
    } catch (error) {
      dispatch(
        appActions.showSnackbarMessage(error, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Error fetching invoices', error);
      setState({ remoteInvoicesStatus: LOAD_STATUS.FAILED });
    }
  };

  /**
   * Handles fetching all active remote document requests for a return
   *
   * @param {number} returnID used to get remote doc requests
   * @param {number} currentPagehelps determine the offset amount when querying for the next page
   * @returns {Promise<boolean>} whether the rows for that page were actually loaded
   */
  const fetchRemoteDocuments = async (returnID, currentPage) => {
    setState({ remoteDocsStatus: LOAD_STATUS.PENDING });
    try {
      const res = await XlinkAPI.getRemoteDocumentRequestsList(returnID, currentPage);

      const rows = asRowArray(res.data);
      if (statusOK(res, false) && rows) {
        dispatch(remoteDocActions.setRemoteDocumentRequests(rows));
        setState({ remoteDocsStatus: LOAD_STATUS.LOADED });
        return true;
      } else {
        setState({ remoteDocsStatus: LOAD_STATUS.FAILED });
      }
    } catch (error) {
      dispatch(
        appActions.showSnackbarMessage(error, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Error opening attachment', error);
      setState({ remoteDocsStatus: LOAD_STATUS.FAILED });
    }

    return false;
  };

  /**
   * Handles fetching the total count of active remote doc requests for this return
   * Since we only fetch 15 at a time, the length of `remoteDocRequestsList` is true total amount.
   *
   * @param {number} returnID used to get remote doc requests
   */
  const fetchRemoteRequestsCount = async returnID => {
    try {
      const res = await XlinkAPI.getRemoteRequestsCount(returnID);
      if (statusOK(res)) {
        dispatch(remoteDocActions.setRemoteRequestsCount(res.data));
      }
    } catch (error) {
      dispatch(
        appActions.showSnackbarMessage(error, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Error getting remote requests count', error);
    }
  };

  /**
   * Uses the local state name of the modal to toggle it off and on
   *
   * @param {string} modalName name of the local state for the modal
   */
  const toggleModal = modalName => {
    setState({ [modalName]: !state[modalName] });
  };

  /** Handles opening/closing the add document modal */
  const toggleAddDocumentModal = () => {
    setState({ isAddDocumentModalOpen: !state.isAddDocumentModalOpen });
  };

  /**
   * used to fetch Attachment from api, and also opens Preview Document modal
   *
   * @param {string} docid used to fetch attachment from api
   * @param {string} name attachments name
   * @param {string} desc attachments description
   * @param {string} type attachments type
   * @param {string} dateCreate attachments created date
   */
  const openAttachment = async (docid, name, desc, type, dateCreat) => {
    try {
      const response = await XlinkAPI.getReturnAttachmentByRIDDocId(returnID, docid);
      if (response) {
        setState({
          currentOpenAttachment: response.data,
          currentOpenAttachmentTitle: name,
          currentOpenAttachmentDesc: desc,
          currentOpenAttachmentType: type,
          currentOpenAttachmentDateUp: dateCreat,
          isPreviewModalOpen: true,
        });
      }
    } catch (error) {
      ErrorHelpers.handleError('Error opening attachment', error);
    }
  };

  /**
   * When the document row is being hovered, the menu button is clickable and a Context Menu drops down.
   * The params help determing which row was clicked aswell as which button.
   *
   * @param {Object} event The event object is used to determine where the user clicked
   * @param {string} selected The selected for which row and btn was selected
   */
  const handleContextMenuToggle = (event, selected) => {
    setState({
      contextMenuAnchor: event.currentTarget,
      contextMenuSelected: selected,
    });
  };

  /**
   * When the user does not select from the dropdown menu, set state back to null and close menu
   */
  const handleContextMenuClose = () => {
    setState({
      contextMenuAnchor: null,
      contextMenuSelected: null,
    });
  };

  const dispatchOpenEmailDialog = (row, tpEmail, spEmail) => {
    const lastName = (row.client_name || '').split(',')[0].trim();
    const rawSSN = (row.ssnein || '').replace(/-/g, '');
    const suggestedPassword = (lastName.slice(0, 4) + rawSSN.slice(-4)).toUpperCase();
    dispatch(formViewerActions.setArchiveDocId(row.document_id));
    dispatch(formViewerActions.setSuggestedPassword(suggestedPassword));
    dispatch(formViewerActions.setEncryptedPdfTitle('Encrypted Document'));
    dispatch(formViewerActions.setEmailBlob(ARCHIVE_EMAIL_BODY));
    dispatch(formViewerActions.setTaxpayerEmail(tpEmail));
    dispatch(formViewerActions.setSpouseEmail(spEmail));
    dispatch(formViewerActions.toggleSuggestedPassword(true));
  };

  /**
   * Opens the Email Encrypted PDF dialog for an archived document.
   * Always triggers a save first so the latest email entered on the Client Data form
   * is flushed to Redux, then waits for the save to complete (signaled by a fresh
   * taxpayerInformation reference) before deciding whether to open the dialog or warn
   * that no email is on file. The save-complete effect below handles both outcomes.
   */
  const openEmailDialog = row => {
    handleContextMenuClose();
    pendingEmailRowRef.current = row;
    props.saveReturn();
  };

  // Fires once return_save completes. taxpayerInformation is re-created on every save,
  // so this runs whether or not an email came back. return_save dispatches run outside
  // React batching, and this effect can flush mid-handler (before setTaxpayerEmail lands),
  // so defer the read to a microtask: by then the entire handler has finished and the
  // store holds the final email. Read straight from the store to avoid a stale closure.
  useEffect(() => {
    const row = pendingEmailRowRef.current;
    if (!row) return;
    pendingEmailRowRef.current = null;
    Promise.resolve().then(() => {
      const tp = store.getState().formViewer.taxpayerEmailForPdf;
      const sp = store.getState().formViewer.spouseEmailForPdf;
      if (tp) {
        dispatchOpenEmailDialog(row, tp, sp);
      } else {
        setState({ isNoEmailModalOpen: true });
      }
    });
  }, [taxpayerInformation]);

  /**
   * Retrieves documents from parent comp, and parses through it to create a Sorted array from the documents.
   * This is then used in getArchiveTable that renders the Table jsx to generate the rows
   *
   * @returns {array} Array of sorted documents
   */
  const getDocumentList = () => {
    const { attachments, newestAttachments } = { ...props };
    let docs = [];

    if (
      attachments &&
      newestAttachments &&
      props.newestAttachments.length < props.attachments.length
    ) {
      docs = attachments.sort((a, b) => parseFloat(b.document_id) - parseFloat(a.document_id));
    } else {
      docs = newestAttachments.sort(
        (a, b) => parseFloat(b.document_id) - parseFloat(a.document_id),
      );
    }

    // Binary attachments have an attachment ID,filter out these copies
    // Documents are seen as seperate entities for each form, do not delete these or attachments won't function
    docs = docs.filter(attachment => {
      return !attachment?.document_attachment_id;
    });
    return docs;
  };

  const getRemoteInvoiceByInvoiceGuid = async invoiceGUID => {
    try {
      const invoiceData = await InvoiceAPI.getRemoteInvoice(
        invoiceGUID,
        state.walletToken,
        state.secToken,
      );
      return invoiceData?.data;
    } catch (err) {
      ErrorHelpers.handleError('Failed to Retrieve Remote Invoice Data: ', err);
    }
  };

  const openRemoteInvoiceModal = invoiceData => {
    dispatch(formViewerActions.toggleRemoteInvoiceModal(true));
    dispatch(
      formViewerActions.setRemoteInvoice({
        ...invoiceData,
      }),
    );
  };

  /**
   * Sends a remote signature request via email or text message to the preparer.
   *
   * @param {object} requestFormData form data from the Request Documents modal
   */
  const sendDocumentRequest = async requestFormData => {
    // USE isEditingRMD for logic if the properties change during eidting
    const preppedData = {
      returnID: parseInt(returnID),
      requesteeType: parseInt(requestFormData.requesteeType), // this needs to come from state to determine if its spouse or taxpayer
      email: requestFormData.email,
      subject: requestFormData.subject,
      description: requestFormData.description,
      documentTypeID: requestFormData.documentTypeID,
      remoteDocumentID: requestFormData.remoteDocumentID || 0, // 0 means that it is a new remote document request
      firmName,
    };

    try {
      // Save the authentication data to the remote docs table for this return
      const res = await XlinkAPI.postRemoteDocumentAuthenticationData(
        preppedData.returnID,
        preppedData.requesteeType,
        preppedData.email,
        preppedData.subject,
        preppedData.description,
        preppedData.documentTypeID,
        preppedData.remoteDocumentID,
        preppedData.firmName,
      );
      if (statusOK(res, false)) {
        fetchRemoteDocuments(returnID, currentPage); // fetch updated documents
        dispatch(remoteDocActions.setCurrentRequestStep(1)); // proceed to next step
        fetchRemoteRequestsCount(returnID);
      }
    } catch (err) {
      dispatch(
        appActions.showSnackbarMessage(err, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Remote Documents Request Error: ', err);
    }
  };

  /**
   * Which table is actually on screen. `selectedHeader` is the user's choice, but
   * the render below falls back to the invoice table when attachments have not
   * arrived, so Print resolves the tab the same way rather than from
   * `selectedHeader` alone - otherwise it can label invoice rows as requested
   * documents and gate on the wrong load.
   *
   * @returns {string} one of ACTIVE_TAB
   */
  const getActiveTab = () => {
    if (props.attachments != null && state.selectedHeader === 1) return ACTIVE_TAB.REQUESTED;
    if (state.selectedHeader === 0) return ACTIVE_TAB.ARCHIVE;
    return ACTIVE_TAB.INVOICES;
  };

  /**
   * Builds the columns/rows for whichever tab is showing, so Print produces the
   * listing the preparer is actually looking at. Desktop behaves the same way -
   * its F8 hook prints the one listview in front of you.
   *
   * Descriptions/subjects print in full rather than through `buildPreviewString`,
   * which truncates purely to keep the on-screen rows a fixed height.
   *
   * @returns {object} { title, columns, rows, columnStyles, emptyText, filename }
   */
  const buildPrintPayload = () => {
    switch (getActiveTab()) {
      case ACTIVE_TAB.REQUESTED: {
        return {
          title: `Requested Documents (page ${currentPage})`,
          columns: REMOTE_COLUMN_HEADERS.filter(column => column !== 'ACTION'),
          rows: getRemoteDocumentList(remoteDocRequestsList || []).map(row => {
            const determineStatus =
              row.is_expired === true && row.document_status !== 2 ? 0 : row.document_status;

            return [
              REQUESTEE_TYPE[row.requestee_type],
              row.subject,
              (documentTypes || []).find(type => row.document_type_id === type.document_type_id)
                ?.document_type,
              convertTimestampToLocal(row.update_date),
              REMOTE_DOC_STATUS_LABELS[determineStatus],
            ];
          }),
          columnStyles: {
            0: { cellWidth: 35 },
            2: { cellWidth: 45 },
            3: { cellWidth: 45 },
            4: { cellWidth: 30 },
          },
          emptyText: 'No requested documents',
          filename: 'requested_documents.pdf',
        };
      }
      case ACTIVE_TAB.INVOICES:
        return {
          title: 'Remote Invoices',
          columns: ['INVOICE', 'STATUS', 'TIME'],
          rows: (state.remoteInvoices || []).map(row => [
            'Remote Invoice',
            row.invoiceData?.invoiceStatus ? row.invoiceData.invoiceStatus : 'Cancelled',
            convertTimestampToLocal(row.createDate),
          ]),
          columnStyles: { 0: { cellWidth: 45 }, 2: { cellWidth: 45 } },
          emptyText: 'No remote invoices',
          filename: 'remote_invoices.pdf',
        };
      default:
        return {
          title: 'Archived Documents',
          columns: COLUMN_HEADERS.filter(column => column !== 'ACTION'),
          rows: getDocumentList().map(row => [
            // Masked on print whether or not the office masks on screen. When the
            // preference already masked it this is a no-op.
            maskSSNEINForPrint(formatMaskSSNEIN(row.ssnein || '', maskSSNEIN, props.isBusiness)),
            row.client_name || '',
            row.document_description,
            row.document_type,
            convertTimestampToLocal(row.create_date),
          ]),
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 45 },
            3: { cellWidth: 45 },
            4: { cellWidth: 45 },
          },
          emptyText: 'No archived documents',
          filename: 'archived_documents.pdf',
        };
    }
  };

  /**
   * The printed page carries this return's identity in its header, so it may only be
   * produced from a dataset that loaded successfully for this return. Pending is not
   * the only unsafe state: a failed fetch leaves the requested-documents list holding
   * the previous return's rows and the invoice list empty, either of which would be
   * printed as fact. Only the two remote tabs load asynchronously here; the archive
   * tab renders props supplied with the return.
   */
  const isActiveTabPrintable = () => {
    switch (getActiveTab()) {
      case ACTIVE_TAB.REQUESTED:
        // documentTypes supplies the DOCUMENT TYPE column; without it every row
        // prints blank.
        return (
          state.remoteDocsStatus === LOAD_STATUS.LOADED &&
          state.documentTypesStatus === LOAD_STATUS.LOADED
        );
      case ACTIVE_TAB.INVOICES:
        return state.remoteInvoicesStatus === LOAD_STATUS.LOADED;
      default:
        return props.attachments != null;
    }
  };

  const handlePrintDocuments = () => {
    printList({ ...buildPrintPayload(), meta: props.printMeta });
  };

  /**
   * Displays table for archived documents
   *
   * @returns {jsx} JSX to render a table
   */
  const getArchiveTable = () => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            {COLUMN_HEADERS.map(column => {
              return (
                <TableCell key={`tableCell${column}`} classes={{ root: classes.cellHeader }}>
                  {column}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {getDocumentList().map((row, i) => {
            return (
              <Fragment key={'archiveFrag-' + i}>
                <TableRow key={'archiveRow-' + row.document_id.toString()} hover>
                  <TableCell
                    key={'archiveSSN-' + row.document_id.toString()}
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                    className={classes.ssnCell}
                  >
                    {formatMaskSSNEIN(row.ssnein || '', maskSSNEIN, props.isBusiness)}
                  </TableCell>
                  <TableCell
                    key={'archiveName-' + row.document_id.toString()}
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    {row.client_name || ''}
                  </TableCell>
                  <TableCell
                    key={'archiveDescription-' + row.document_id.toString()}
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    {buildPreviewString(row.document_description)}
                  </TableCell>
                  <TableCell
                    key={'archiveType-' + row.document_id.toString()}
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    {row.document_type}
                  </TableCell>
                  <TableCell
                    key={'archiveDate-' + row.document_id.toString()}
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    {convertTimestampToLocal(row.create_date)}
                  </TableCell>
                  <TableCell
                    key={'archiveAction-' + row.document_id.toString()}
                    variant="body"
                    classes={{ root: classes.cellRoot }}
                    component="th"
                    scope="row"
                  >
                    <div className={classes.actionCellContent}>
                      {/* All row actions (including Preview) live in the always-visible
                          skewer menu, matching the Signatures page Action column. */}
                      <div className={classes.actionMenuWrapper}>
                        <Button
                          className={`contextMenuButton ${classes.contextMenuButton}`}
                          aria-haspopup="true"
                          aria-owns={open ? 'button-menu' + row.document_id : null}
                          onClick={e => {
                            // The menu opens even on locked returns so read-only actions
                            // (Preview) stay reachable, matching the old standalone Preview
                            // button; mutating items below disable themselves when locked.
                            handleContextMenuToggle(e, 'button-menu' + row.document_id);
                          }}
                        >
                          <img src={menuVertical} className={classes.contextMenuIcon} />
                        </Button>
                        <Menu
                          id={'button-menu' + row.document_id}
                          anchorEl={state.contextMenuAnchor}
                          open={Boolean(
                            state.contextMenuSelected === 'button-menu' + row.document_id,
                          )}
                          onClose={handleContextMenuClose}
                        >
                          <MenuItem
                            id={`archiveRowPreviewButton-${i}`}
                            onClick={() => {
                              handleContextMenuClose();
                              openAttachment(
                                row.document_id,
                                row.document_name,
                                row.document_description,
                                row.document_type,
                                row.create_date.substring(0, 10),
                              );
                            }}
                          >
                            Preview
                          </MenuItem>
                          <AccessControl
                            requiredAction="write"
                            accessLevel="delete_document_from_archive"
                            disableOnFalse={true}
                          >
                            <MenuItem
                              disabled={
                                props.locked ||
                                UNDELETABLE_DOC_TYPES.includes(row.document_type) ||
                                row.is_signature_doc
                              }
                              onClick={e => {
                                handleContextMenuClose();
                                setState({ docID: row.document_id });
                                toggleModal('isDocArchiveConfirmDeleteModalOpen');
                              }}
                            >
                              Delete
                            </MenuItem>
                          </AccessControl>
                          <MenuItem
                            disabled={props.locked}
                            onClick={() => {
                              handleContextMenuClose();
                              openAttachment(
                                row.document_id,
                                row.document_name,
                                row.document_description,
                                row.document_type,
                                row.create_date.substring(0, 10),
                              );
                            }}
                          >
                            Download
                          </MenuItem>
                          <MenuItem disabled={props.locked} onClick={() => openEmailDialog(row)}>
                            Email
                          </MenuItem>
                        </Menu>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const getInvoiceTable = () => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell classes={{ root: classes.cellHeader }}>INVOICE </TableCell>
            <TableCell classes={{ root: classes.cellHeader }}>STATUS </TableCell>
            <TableCell classes={{ root: classes.cellHeader }} />
            <TableCell classes={{ root: classes.cellHeader }}>TIME </TableCell>
            <TableCell classes={{ root: classes.cellHeader }}>ACTION</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* An unestablished list is not the same as an empty one - say so on the tab
              rather than leaving Print greyed out with a blank table and no reason. */}
          {state.remoteInvoicesStatus === LOAD_STATUS.FAILED && !state.remoteInvoices?.length ? (
            <TableRow>
              <TableCell variant="body" classes={{ root: classes.cellRoot }} colSpan={5}>
                Unable to load remote invoices.
              </TableCell>
            </TableRow>
          ) : null}
          {state.remoteInvoices ? (
            state.remoteInvoices.map((row, i) => {
              return (
                <Fragment key={'remoteFrag-' + i}>
                  <TableRow key={'remoteRow-' + i}>
                    <TableCell
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      Remote Invoice
                    </TableCell>
                    <TableCell
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      {row.invoiceData?.invoiceStatus
                        ? row.invoiceData?.invoiceStatus
                        : 'Cancelled'}
                    </TableCell>
                    <TableCell
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    ></TableCell>
                    <TableCell
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      {convertTimestampToLocal(row.createDate)}
                    </TableCell>
                    <TableCell
                      align="right"
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      <div style={{ display: 'inline-flex', width: '70px' }}>
                        <Button
                          size="small"
                          id={`archiveRowPreviewButton-${i}`}
                          classes={{ root: classes.previewButton }}
                          onClick={() => openRemoteInvoiceModal(row.invoiceData)}
                        >
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })
          ) : (
            <></>
          )}
        </TableBody>
      </Table>
    );
  };

  // Handles retrieving the selected remote document id and sending the request to delete the remote document request
  const deleteRemoteDocRequest = async remoteDocumentID => {
    try {
      props.toggleRequestDocsModal();
      dispatch(remoteDocActions.setIsEditingRMD(false));
      dispatch(remoteDocActions.resetRMDFormFields());

      // delete the selected document
      await XlinkAPI.deleteRemoteDocumentRequest(returnID, remoteDocumentID);

      dispatch(remoteDocActions.deleteRMDRequest(remoteDocumentID));

      fetchRemoteRequestsCount(returnID);
      dispatch(
        appActions.showSnackbarMessage('Document has been deleted.', 'success', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
    } catch (err) {
      dispatch(
        appActions.showSnackbarMessage(err, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Delete Remote Document Error: ', err);
    }
  };

  /**
   * Handles pagination change for remote document requests.
   *
   * @param {number} page
   */
  const handlePageChange = async page => {
    if (state.remoteDocsStatus === LOAD_STATUS.PENDING) return;

    const requestedPage = page.selected + 1;

    if (await fetchRemoteDocuments(returnID, requestedPage)) {
      dispatch(remoteDocActions.setCurrentPage(requestedPage));
    }
  };

  /**
   * Handles building the status ui using the document status
   *
   * @param {number} status document status used to determine ui
   * @returns
   */
  const buildStatusUI = status => {
    const label = REMOTE_DOC_STATUS_LABELS[status];
    const icon =
      status === 0 ? (
        <ErrorOutlineIcon className={classes.statusIconStyle} />
      ) : status === 1 ? (
        <WarningAmberIcon className={classes.statusIconStyle} />
      ) : (
        <CheckIcon className={classes.statusIconStyle} />
      );

    return (
      <div className={`doc-archive-rmd-list-status-icon remote-doc-status ${label.toLowerCase()}`}>
        {icon}
        {label}
      </div>
    );
  };

  // Handles building the UI for the remote document requests Table
  const getRemoteDocumentsTable = () => {
    const totalPages = Math.ceil(totalRequestsCount / 15); // the backend has limit of 15 hardcoded

    return (
      <>
        <Table>
          <TableHead>
            <TableRow>
              {REMOTE_COLUMN_HEADERS.map(column => {
                return (
                  <TableCell key={`tableCell${column}`} classes={{ root: classes.cellHeader }}>
                    {column}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {remoteDocRequestsList?.length > 0 &&
              getRemoteDocumentList(remoteDocRequestsList).map((row, i) => {
                // if document status is already in complete state, then do not display the expired icon.
                const determineStatus =
                  row.is_expired === true && row.document_status !== 2 ? 0 : row.document_status;

                // documentTypes has the current doc types lookup id references
                const documentTypeName = documentTypes.find(
                  type => row.document_type_id === type.document_type_id,
                )?.document_type;

                return (
                  <TableRow key={`remoteDocRow-${row.remote_document_id}`} hover>
                    <TableCell
                      key={`personRequestingType-${i}`}
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      {REQUESTEE_TYPE[row.requestee_type]}
                    </TableCell>
                    <TableCell
                      key={`subject-${i}`}
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      {buildPreviewString(row.subject)}
                    </TableCell>
                    <TableCell
                      key={`documentType-${i}`}
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      {documentTypeName}
                    </TableCell>
                    <TableCell
                      key={`updateDate-${i}`}
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      {convertTimestampToLocal(row.update_date)}
                    </TableCell>
                    <TableCell
                      key={`documentStatus-${i}`}
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      {buildStatusUI(determineStatus)}
                    </TableCell>
                    <TableCell
                      key={`remoteDocAction-${i}`}
                      variant="body"
                      classes={{ root: classes.cellRoot }}
                      component="th"
                      scope="row"
                    >
                      <div style={{ display: 'inline-flex', width: '70px' }}>
                        <Button
                          size="small"
                          id={`archiveRowPreviewButton-${i}`}
                          classes={{ root: classes.previewButton }}
                          onClick={() => {
                            props.toggleRequestDocsModal();
                            dispatch(remoteDocActions.setSelectedRemoteDoc(row));
                            dispatch(remoteDocActions.setIsEditingRMD(true));
                          }}
                          disabled={props.locked}
                        >
                          Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>

        <div
          className={
            state.remoteDocsStatus === LOAD_STATUS.PENDING ? classes.paginationLoading : undefined
          }
        >
          <ReactPaginate
            onPageChange={page => handlePageChange(page)}
            pageCount={totalPages}
            initialPage={currentPage - 1}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            forcePage={currentPage - 1}
            previousLabel={'<'}
            nextLabel={'>'}
            breakLabel={'...'}
            breakClassName={'paginate-break'}
            pageClassName={totalPages === 0 ? 'paginate-hide' : 'paginate-page'}
            activeClassName={totalPages <= 1 ? 'paginate-hide' : 'paginate-activepage'}
            previousClassName={totalPages <= 1 ? 'paginate-hide' : 'paginate-prev'}
            nextClassName={totalPages <= 1 ? 'paginate-hide' : 'paginate-next'}
            disabledClassName={'paginate-disabled'}
            containerClassName={'pagination'}
            subContainerClassName={'pages pagination'}
          />
        </div>
      </>
    );
  };

  return (
    <div id="taxform" className={classes.archiveDocContainer}>
      <div className={classes.archiveHeader}>
        <Grid container spacing={8}>
          <Grid container justify="flex-start">
            <ClickableTypography
              onClick={() => setState({ selectedHeader: 0 })}
              className={`${classes.archiverHeaderTitle} ${
                state.selectedHeader === 0
                  ? classes.selectedHeaderStyle
                  : classes.unselectedHeaderStyle
              }`}
              variant="h6"
            >
              Archived Documents
            </ClickableTypography>
            <Typography variant="h6" className={classes.archiveHeaderVertDivider}></Typography>
            <ClickableTypography
              onClick={() => setState({ selectedHeader: 1 })}
              className={`${classes.archiverHeaderTitle} ${
                state.selectedHeader === 1
                  ? classes.selectedHeaderStyle
                  : classes.unselectedHeaderStyle
              }`}
              variant="h6"
            >
              Requested Documents
            </ClickableTypography>
            <Typography variant="h6" className={classes.archiveHeaderVertDivider}></Typography>
            <ClickableTypography
              onClick={() => setState({ selectedHeader: 2 })}
              className={`${classes.archiverHeaderTitle} ${
                state.selectedHeader === 2
                  ? classes.selectedHeaderStyle
                  : classes.unselectedHeaderStyle
              }`}
              variant="h6"
            >
              Remote Invoices
            </ClickableTypography>
          </Grid>
          <Grid container justify="flex-end">
            <PrintListButton
              id="btnPrintDocArchive"
              className={classes.addDocButton}
              size="small"
              onClick={handlePrintDocuments}
              disabled={!isActiveTabPrintable()}
            />
            {state.selectedHeader === 0 ? (
              <Button
                id="btnOpenAttachModalDocArchive"
                className={classes.addDocButton}
                size="small"
                color="primary"
                onClick={() => {
                  if (!props.locked) {
                    toggleAddDocumentModal();
                  }
                }}
                disabled={props.locked}
              >
                Add New Documents
              </Button>
            ) : null}
            {state.selectedHeader === 1 ? (
              <Button
                id="btnOpenRequestModalDocArchive"
                className={classes.addDocButton}
                size="small"
                color="primary"
                onClick={() => {
                  props.saveReturn();
                  props.toggleRequestDocsModal();
                }}
                disabled={props.locked}
              >
                Request Documents
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </div>
      <div>
        {getActiveTab() === ACTIVE_TAB.REQUESTED
          ? getRemoteDocumentsTable()
          : getActiveTab() === ACTIVE_TAB.ARCHIVE
          ? getArchiveTable()
          : getInvoiceTable()}
      </div>
      <hr className="doc-archive-table-div-hr" />
      {state.isAddDocumentModalOpen && (
        <AddDocumentModal
          handleCloseDocumentModalOk={props.closeAddDocumentModalOk}
          showSnackBarErr={props.showSnackBarErr}
          isAddDocumentModalOpen={state.isAddDocumentModalOpen}
          toggleAddDocumentModal={toggleAddDocumentModal}
        />
      )}
      {props.isRequestDocumentModalOpen && (
        <RequestDocumentsModal
          toggleModal={toggleModal}
          showSnackBarErr={props.showSnackBarErr}
          isRequestDocumentModalOpen={props.isRequestDocumentModalOpen}
          clientFilingStatus={clientFilingStatus}
          toggleRequestDocsModal={props.toggleRequestDocsModal}
          sendDocumentRequest={sendDocumentRequest}
          returnID={returnID}
          isBusiness={props.isBusiness}
          taxpayerEmail={taxpayerEmailForPdf}
          spouseEmail={spouseEmailForPdf}
          rmdFormFields={rmdFormFields}
          isEditingRMD={isEditingRMD}
          currentRequestStep={currentRequestStep}
          documentTypes={documentTypes}
        />
      )}
      {state.isPreviewModalOpen && (
        <PreviewDocumentModal
          toggleModal={toggleModal}
          isPreviewModalOpen={state.isPreviewModalOpen}
          currentOpenAttachment={state.currentOpenAttachment}
          currentOpenAttachmentDesc={state.currentOpenAttachmentDesc}
          currentOpenAttachmentTitle={state.currentOpenAttachmentTitle}
          currentOpenAttachmentDateUp={state.currentOpenAttachmentDateUp}
          currentOpenAttachmentType={state.currentOpenAttachmentType}
        />
      )}
      {state.isRequestPDFModalOpen && (
        <RequestPDFModal
          toggleModal={toggleModal}
          isRequestPDFModalOpen={state.isRequestPDFModalOpen}
          currentOpenAttachment={state.currentOpenAttachment}
          currentOpenAttachmentType={state.currentOpenAttachmentType}
          currentOpenAttachmentDesc={state.currentOpenAttachmentDesc}
        />
      )}
      {state.isConfirmDeleteModalOpen && (
        <SimpleDialog
          open={state.isConfirmDeleteModalOpen}
          onClose={() => toggleModal('isConfirmDeleteModalOpen')}
          onConfirm={() => {
            toggleModal('isConfirmDeleteModalOpen');
            deleteRemoteDocRequest(rmdFormFields.remoteDocumentID);
          }}
          dialogTitle="WARNING"
          contentText="Are you sure you want to Delete this remote document request?"
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
      {state.isDocArchiveConfirmDeleteModalOpen && (
        <SimpleDialog
          open={state.isDocArchiveConfirmDeleteModalOpen}
          onClose={() => toggleModal('isDocArchiveConfirmDeleteModalOpen')}
          onConfirm={() => {
            toggleModal('isDocArchiveConfirmDeleteModalOpen');
            props.removeDocument(state.docID);
          }}
          dialogTitle="WARNING"
          contentText="By confirming you are deleting this document and it will not be recoverable."
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
      {state.isNoEmailModalOpen && (
        <SimpleDialog
          open={state.isNoEmailModalOpen}
          onConfirm={() => setState({ isNoEmailModalOpen: false })}
          dialogTitle="No Email Address on File"
          contentText="No email address is on file for this client. Add one on the Client Data form to email this document."
          confirmText="OK"
        />
      )}
    </div>
  );
};

export default withStyles(styles)(ArchivedDocuments);
